import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { queuedTimeSeriesActionCreator } from '../../redux/actions/timeseries';
import getSeries from '../../redux/selectors/timeseries';
import TrendGraph from './TrendGraph';
import { getPointName as gpn, getPointMappings } from '../../redux/selectors/points';
import RangePicker, { daysAgo } from './RangePicker';
import SeriesPicker from './SeriesPicker';
import { roundIntsOnly } from '../../utils/parsing';

import './PointTrendContainer.css';

const getData = (data, xAxis, mapping) => {
  const valuesToReturn = new Array(xAxis.length);
  const lookup = data.reduce((map, obj) => {
    const { timestamp } = obj;
    const value = roundIntsOnly(obj.value);
    const mapped = mapping[value];
    // eslint-disable-next-line no-param-reassign
    map[timestamp] = [timestamp, mapped || value];
    return map;
  }, {});

  for (let i = 0; i < xAxis.length; i += 1) {
    const timestamp = xAxis[i];
    // eslint-disable-next-line no-prototype-builtins
    if (lookup.hasOwnProperty(timestamp)) {
      valuesToReturn[i] = lookup[timestamp];
    } else {
      valuesToReturn[i] = null;
    }
  }
  return valuesToReturn;
};

const getXAxis = (series) => {
  const allTimestamps = new Set();
  for (let i = 0; i < series.length; i += 1) {
    series[i].values.forEach((v) => allTimestamps.add(v.timestamp));
  }
  return Array.from(allTimestamps).sort();
};

/**
 * For numerical data, label the y axis with the displayed unit
 *
 * For categorical (any of our 'Ordinal' or 'Enum' types) data, format the y axis to
 * map the data data. For example https://echarts.apache.org/examples/en/editor.html?c=line-y-category
 */
const getYAxes = (mappings) =>
  mappings.map((m) =>
    m.data?.length
      ? {
          type: 'category',
          data: m.data,
          triggerEvent: false,
          splitArea: { show: true },
          axisLabel: {
            showMinLabel: true,
            showMaxLabel: true,
            // note: if we ever decide to force-show all labels then this is the echarts config setting
            // interval: 0,
          },
        }
      : {
          type: 'value',
          name: m.unit,
          nameLocation: 'center',
          // This sometimes overlaps when the axis units get extremely large (BTU)
          // See unresolved bug: https://github.com/apache/incubator-echarts/issues/9265
          // or our Jira issue: APP-152
          nameGap: 40,
        }
  );

const formatSeriesForGraph = (series, xAxis, getSeriesName, pointIdToAxis, mappings) => {
  const formatted = [];
  if (!series) return formatted;
  for (let i = 0; i < series.length; i += 1) {
    const pid = series[i].pointId;
    const axis = pointIdToAxis[pid] || 0;
    const mapping = mappings[axis]?.data;
    const newSeries = {
      id: pid,
      name: getSeriesName(pid),
      type: 'line',
      data: getData(series[i].values, xAxis, mapping),
      yAxisIndex: axis,
    };
    formatted.push(newSeries);
  }
  return formatted;
};

// call this in render() in order to help debug chart display w/ categorical data
// eslint-disable-next-line no-unused-vars
const debugMakeOneOfEveryValue = (pointSeries, mappings) => {
  if (pointSeries.length) {
    for (let i = 0; i < mappings[0].data.length; i += 1) {
      // eslint-disable-next-line no-param-reassign
      pointSeries[0].values[i + 1].value = `${i}`;
    }
  }
};

const INITIAL_POINTS = 5;

class PointTrendContainer extends React.PureComponent {
  state = {
    start: daysAgo(1),
    end: Date.now(),
    selectedPointIds: null,
  };

  componentDidMount() {
    this.fetchData();
  }

  getSelectedPoints() {
    const { selectedPointIds } = this.state;
    return selectedPointIds != null
      ? selectedPointIds
      : this.props.pointIds.slice(0, INITIAL_POINTS) || [];
  }

  // similar to logic in the series picker
  getSeriesName = (pointId) => {
    const { getSeriesName } = this.props;
    const name = getSeriesName(pointId);
    return name;
  };

  fetchData() {
    const { start, end } = this.state;
    if (!start || !end) return;
    // delightfully, javascript can pass a `Date` object of 'Invalid Date' which
    // is non-null but returns NaN from getTime()
    if (Number.isNaN(start) || Number.isNaN(end)) return;
    this.props.fetchData(this.getSelectedPoints(), start, end);
  }

  render() {
    const { start, end } = this.state;
    const selectedPoints = this.getSelectedPoints();
    const { getMappings, title, timezone } = this.props;
    const { mappings, pointIdToAxis } = getMappings(selectedPoints);
    const pointSeries = this.props.getSeries(selectedPoints);
    const yAxes = getYAxes(mappings);
    const xAxisData = getXAxis(pointSeries);
    const series = formatSeriesForGraph(
      pointSeries,
      xAxisData,
      this.getSeriesName,
      pointIdToAxis,
      mappings
    );
    const labels = series.map((s) => s.name);
    return (
      <div className="PointTrendContainer">
        <div className="trend-range-picker">
          <span>Data range: </span>
          <RangePicker
            from={start}
            to={end}
            timezone={timezone}
            onChange={({ from, to }) => this.setState({ start: from, end: to }, this.fetchData)}
          />
          {this.props.pointIds.length > 1 ? (
            <>
              <span className="trend-picker-label">Active series:</span>
              <SeriesPicker
                selectedPointIds={selectedPoints}
                onChange={(selectedPointIds) => this.setState({ selectedPointIds }, this.fetchData)}
                pointIds={this.props.pointIds}
              />{' '}
            </>
          ) : null}
        </div>
        <TrendGraph
          XAxisData={xAxisData}
          yAxis={yAxes}
          series={series}
          labels={labels}
          title={title}
          timezone={timezone}
        />
      </div>
    );
  }
}

PointTrendContainer.propTypes = {
  title: PropTypes.string.isRequired,
  pointIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  getSeries: PropTypes.func.isRequired,
  getSeriesName: PropTypes.func.isRequired,
};

PointTrendContainer.defaultProps = {};

export default connect(
  (state, { pointIds }) => ({
    getSeries: (selectedPointIds) => getSeries(state, selectedPointIds),
    getMappings: (selectedPointIds) => getPointMappings(state, selectedPointIds),
    getSeriesName: (pointId) => gpn(state, pointId),
    timezone: (() => {
      const pointId = pointIds && pointIds[0];
      const buildingId = state.entities.points.byId[pointId]?.buildingId;
      const timezone = state.entities.buildings.byId[buildingId]?.timezone;
      return timezone;
    })(),
  }),
  (dispatch) => ({
    fetchData: (pids, start, end) => dispatch(queuedTimeSeriesActionCreator(pids, start, end)),
  })
)(PointTrendContainer);
